import React from 'react'
import { Box, Tooltip } from '@mui/material'
import { ArrowDown, ArrowUp, ArrowsDownUp } from '@phosphor-icons/react'
import DataTable from './DataTable'

/**
 * Tabla con controles de ordenamiento en los encabezados, basada en DataTable.
 * La pantalla conserva el orden actual y decide si ordena las filas localmente
 * o consulta al servidor; este componente sólo notifica la columna seleccionada.
 *
 * Datos que recibe:
 * - `columns`: columnas de DataTable con `{ key, label }`. `sortable: true`
 *   reemplaza su encabezado por un botón con icono y tooltip de MUI.
 *   Las columnas sin `sortable` conservan su encabezado original.
 * - `sortBy`: clave de la columna cuyo orden está activo.
 * - `sortDirection`: `asc` o `desc`; controla el icono y el atributo aria-sort.
 * - `onSort(columnKey)`: se ejecuta al pulsar un encabezado ordenable. La pantalla
 *   debe actualizar sortBy, sortDirection y los datos; no hay un ciclo de orden interno.
 * - Las demás props, como `rows`, `rowKey`, `loading` y `pagination`, se pasan
 *   a DataTable. Por defecto conserva el encabezado cuando no hay filas.
 *
 * Los estilos están en src/css/components/SortableDataTable.scss.
 *
 * Ejemplo de ordenamiento controlado por la pantalla:
 *   <SortableDataTable
 *     columns={[{ key: 'amount', label: 'Monto', sortable: true, align: 'right' }]}
 *     rows={filas}
 *     sortBy={columnaOrdenada}
 *     sortDirection={direccion}
 *     onSort={cambiarOrden}
 *   />
 */
export default function SortableDataTable({
  columns = [],
  sortBy,
  sortDirection,
  onSort,
  ...props
}) {
  const sortableColumns = columns.map((column) => {
    if (!column.sortable) return column
    const active = sortBy === column.key
    const descending = sortDirection === 'desc'
    const Icon = active ? (descending ? ArrowDown : ArrowUp) : ArrowsDownUp

    return {
      ...column,
      ariaSort: active ? (descending ? 'descending' : 'ascending') : 'none',
      headerRender: () => (
        <Tooltip
          title={`Ordenar por ${column.label}${active ? `. Orden actual: ${descending ? 'descendente' : 'ascendente'}` : ''}`}
          placement="top"
          arrow
          describeChild
        >
          <Box
            component="button"
            type="button"
            className="sortable-data-table__sort-button"
            aria-label={`Ordenar por ${column.label}`}
            onClick={() => onSort?.(column.key)}
          >
            {column.label}
            <Icon size={13} aria-hidden="true" />
          </Box>
        </Tooltip>
      )
    }
  })

  return <DataTable keepHeaderWhenEmpty {...props} columns={sortableColumns} />
}
